import { useCallback, useEffect, useState } from "react";
import { chatList, clearNotification, deleteChat as deleteChatApi } from "../../api/user";
import { toDate } from "../../utils/timeFormat";
import {
  countUnreadThreads,
  isThreadUnread,
  publishBadgeCounts,
} from "../../utils/unreadBadges";

// The Chats inbox: fetch, sort, unread, delete. Shared by the desktop two-panel
// screen and the mobile list so both show the same rows in the same order.
export default function useChatList() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  // Opening a thread writes a read watermark to localStorage, which the unread
  // predicate reads — bumping this re-renders the rows so the dot clears without
  // a refetch.
  const [seenTick, setSeenTick] = useState(0);
  const me = (() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo"))?.id ?? null;
    } catch (e) {
      return null;
    }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await chatList(1);
      const raw = response?.data?.data?.data || [];
      const rows = raw
        .filter((item) => item?.last_message?.id)
        .filter((item) => item?.friend_details?.id != 0)
        // Sort strictly by recency, newest first. Unread does NOT affect
        // position.
        .sort(
          (a, b) =>
            (toDate(b?.last_message?.created_at)?.getTime() || 0) -
            (toDate(a?.last_message?.created_at)?.getTime() || 0)
        );
      setThreads(rows);
    } catch (error) {
      console.error("Error loading chats:", error);
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    load();
    // Drop the server-side badge for messages; from here on the row-level
    // predicate is what drives the header dot.
    clearNotification(2).catch(() => {});
  }, [load]);

  // Throws on failure so the caller can surface the API's own message.
  const removeThread = async (chat) => {
    const response = await deleteChatApi(chat?.last_message?.chat_group_id);
    if (!response?.data) return null;
    await load();
    return response.data.message;
  };

  const refreshUnread = () => setSeenTick((n) => n + 1);

  // Header glyph follows exactly what the rows show. Gated on !loading so the
  // first render (threads still empty) doesn't blink the dot off and back on.
  useEffect(() => {
    if (loading) return;
    publishBadgeCounts({ messageCount: countUnreadThreads(threads, me) });
  }, [threads, seenTick, me, loading]);

  return {
    threads,
    loading,
    me,
    reload: load,
    removeThread,
    refreshUnread,
    isUnread: (chat) => isThreadUnread(chat, me),
  };
}
